import styled, { keyframes } from "styled-components";
import { Modal } from "antd";
import Colors from "../../../../themes/Colors";
import { getDomain } from "../../../../services/utils";

const domain = getDomain(window.location.hostname);
const isLatinAd = domain === "latinad";

const accentColor = isLatinAd ? "#60A5FA" : Colors.primary;
const chatBackground = isLatinAd
  ? "linear-gradient(to bottom right, #0F172A, #1E3A8A, #0F172A)"
  : Colors.background;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px) scale(1); opacity: 0.4; }
  50% { transform: translateY(-20px) scale(1.2); opacity: 0.8; }
`;

const typingPulse = keyframes`
  0%, 100% { opacity: 0.3; transform: translateY(0); }
  50% { opacity: 1; transform: translateY(-3px); }
`;

const MapAIChatbotHolder = styled(Modal)`
  /* Anchor the chat to the bottom-right corner (above the WhatsApp / launcher
     buttons) instead of the default centered position. */
  &.ant-modal {
    position: fixed;
    right: 2%;
    bottom: calc(4% + 140px);
    top: auto;
    margin: 0;
    padding: 0;
    max-width: 92vw;
  }

  & .ant-modal-content {
    border-radius: 16px;
    overflow: hidden;
  }
  & .ant-modal-body {
    padding: 0;
  }
  & .map-ai-chatbot {
    position: relative;
    display: flex;
    flex-direction: column;
    height: 70vh;
    max-height: 620px;
    background: ${chatBackground};
    overflow: hidden;
  }

  & .chatbot-particles {
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 0;
  }
  & .chatbot-float-particle {
    position: absolute;
    width: 4px;
    height: 4px;
    border-radius: 9999px;
    background-color: ${accentColor};
    opacity: 0.4;
    animation: ${float} 3s ease-in-out infinite;
  }

  & .chatbot-header {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 18px 20px;
    border-bottom: 1px solid ${isLatinAd ? 'rgba(96, 165, 250, 0.25)' : `${Colors.primaryAccent}40`};
  }
  & .chatbot-avatar {
    position: relative;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  & .chatbot-avatar-icon {
    width: 34px;
    height: 34px;
    object-fit: contain;
    z-index: 1;
  }
  & .chatbot-header-text {
    display: flex;
    flex-direction: column;
  }
  & .chatbot-title {
    color: ${isLatinAd ? '#fff' : Colors.coal};
    font-size: 16px;
    font-weight: 600;
    line-height: 1.2;
  }
  & .chatbot-subtitle {
    color: ${isLatinAd ? '#94a3b8' : Colors.grayMedium};
    font-size: 12px;
  }

  & .chatbot-messages {
    position: relative;
    z-index: 1;
    flex: 1;
    overflow-y: auto;
    padding: 16px 20px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    scrollbar-width: thin;
    scrollbar-color: ${accentColor}4D transparent;
  }
  & .chatbot-messages::-webkit-scrollbar {
    width: 6px;
  }
  & .chatbot-messages::-webkit-scrollbar-track {
    background: transparent;
  }
  & .chatbot-messages::-webkit-scrollbar-thumb {
    background-color: ${accentColor}4D;
    border-radius: 9999px;
  }
  & .chatbot-messages::-webkit-scrollbar-thumb:hover {
    background-color: ${accentColor}80;
  }
  & .chatbot-bubble {
    max-width: 80%;
    padding: 10px 14px;
    border-radius: 14px;
    font-size: 14px;
    line-height: 1.5;
    word-break: break-word;
    white-space: pre-wrap;
  }
  & .chatbot-bubble--user {
    align-self: flex-end;
    background: ${isLatinAd ? 'linear-gradient(to left, #218ae7, #29509f)' : Colors.primary};
    color: ${Colors.white};
    border-bottom-right-radius: 4px;
  }
  & .chatbot-bubble--assistant {
    align-self: flex-start;
    background: rgba(255, 255, 255, 0.08);
    color: ${isLatinAd ? '#e2e8f0' : Colors.coal};
    border: 1px solid ${accentColor}4D;
    border-bottom-left-radius: 4px;
    backdrop-filter: blur(4px);
  }
  & .chatbot-bubble--error {
    align-self: flex-start;
    background: rgba(239, 68, 68, 0.2);
    color: ${isLatinAd ? '#fca5a5' : Colors.red};
    border: 1px solid rgba(239, 68, 68, 0.4);
    border-bottom-left-radius: 4px;
  }
  & .chatbot-bubble--system {
    align-self: center;
    max-width: 90%;
    text-align: center;
    background: ${Colors.atention}1F;
    color: ${Colors.atention};
    border: 1px solid ${Colors.atention}4D;
    font-size: 12px;
  }
  & .chatbot-bubble--system .anticon {
    margin-right: 4px;
  }
  & .chatbot-typing {
    display: flex;
    align-items: center;
    gap: 4px;
  }
  & .chatbot-dot {
    width: 6px;
    height: 6px;
    border-radius: 9999px;
    background-color: ${accentColor};
    animation: ${typingPulse} 1.2s infinite ease-in-out;
  }
  & .chatbot-dot:nth-child(2) {
    animation-delay: 0.2s;
  }
  & .chatbot-dot:nth-child(3) {
    animation-delay: 0.4s;
  }

  & .chatbot-quick-actions {
    position: relative;
    z-index: 1;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding: 8px 20px;
  }
  & .chatbot-chip {
    background:  ${isLatinAd ? 'rgba(96, 165, 250, 0.12)' : `${Colors.primary}1F`};
    border: 1px solid  ${isLatinAd ? 'rgba(96, 165, 250, 0.35)' : `${Colors.primary}40`};
    color: ${accentColor};
    font-size: 12px;
    padding: 4px 10px;
    border-radius: 9999px;
    cursor: pointer;
    transition: background 0.2s ease;
  }
  & .chatbot-chip:hover {
    background: ${isLatinAd ? 'rgba(96, 165, 250, 0.25)' : `${Colors.primary}40`};
  }

  & .chatbot-header-actions {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 6px;
  }
  & .chatbot-header-btn.ant-btn {
    background: transparent;
    border: 1px solid rgba(96, 165, 250, 0.3);
    color: ${isLatinAd ? '#94a3b8' : Colors.grayMedium};
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  & .chatbot-header-btn.ant-btn:hover,
  & .chatbot-header-btn.ant-btn:focus {
    background: rgba(96, 165, 250, 0.12);
    border-color: ${accentColor};
    color: ${accentColor};
  }
  & .chatbot-header-btn.ant-btn[disabled],
  & .chatbot-header-btn.ant-btn[disabled]:hover,
  & .chatbot-header-btn.ant-btn[disabled]:focus,
  & .chatbot-header-btn.ant-btn[disabled]:active {
    background: transparent;
    border-color: rgba(96, 165, 250, 0.15);
    color: ${isLatinAd ? '#475569' : Colors.grayMedium};
    opacity: 0.6;
    cursor: not-allowed;
    box-shadow: none;
  }

  & .chatbot-suggest-reset {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 20px;
    background: ${Colors.atention}1F;
    color: ${Colors.atention};
    font-size: 12px;
    margin: 0px 20px;
    border-radius: 8px;
  }
  & .chatbot-suggest-reset .anticon {
    flex-shrink: 0;
  }
  & .chatbot-suggest-reset .ant-btn {
    margin-left: auto;
    height: 24px;
    padding: 0 10px;
    font-size: 12px;
    background: ${Colors.atention}33;
    border-color: ${Colors.atention}80;
    color: ${Colors.atention};
    border-radius: 6px;
  }

  & .chatbot-proposal-card {
    align-self: flex-start;
    width: 100%;
    max-width: 100%;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid ${isLatinAd ? 'rgba(96, 165, 250, 0.3)' : `${Colors.primaryAccent}4D`};
    border-radius: 12px;
  }
  & .chatbot-proposal-card--resolved {
    opacity: 0.55;
  }
  & .chatbot-proposal-body {
    padding: 12px 14px;
  }
  & .chatbot-proposal-reason {
    color: ${isLatinAd ? '#94a3b8' : Colors.coal};
    font-size: 12px;
    margin-bottom: 10px;
  }
  & .chatbot-proposal-subreason {
    color: ${isLatinAd ? '#64748b' : Colors.grayMedium};
    font-size: 11px;
    margin-top: 8px;
    font-style: italic;
  }
  & .chatbot-proposal-display-row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    cursor: pointer;
    color: ${isLatinAd ? '#e2e8f0' : Colors.coal};
  }
  & .chatbot-proposal-display-row:last-child {
    border-bottom: none;
  }
  & .chatbot-proposal-display-thumb {
    width: 36px;
    height: 36px;
    flex-shrink: 0;
    border-radius: 6px;
    object-fit: cover;
    background: rgba(255, 255, 255, 0.06);
  }
  & .chatbot-proposal-display-thumb--empty {
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${isLatinAd ? '#475569' : Colors.grayMedium};
    font-size: 16px;
    border: 1px solid rgba(255, 255, 255, 0.08);
  }
  & .chatbot-proposal-display-info {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
  }
  & .chatbot-proposal-display-name {
    font-weight: 500;
    font-size: 13px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: ${isLatinAd ? 'inherit' : Colors.coal};
  }
  & .chatbot-proposal-display-loc {
    color: ${isLatinAd ? '#64748b' : Colors.grayMedium};
    font-size: 11px;
  }
  & .chatbot-proposal-display-price {
    color: ${accentColor};
    font-size: 12px;
    white-space: nowrap;
  }
  & .chatbot-proposal-counter {
    margin-top: 8px;
    text-align: right;
    color: ${isLatinAd ? '#64748b' : Colors.grayMedium};
    font-size: 11px;
  }
  & .chatbot-proposal-change-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 0;
    color: ${isLatinAd ? '#94a3b8' : Colors.grayMedium};
    font-size: 13px;
  }
  & .chatbot-proposal-change-row .anticon {
    color: ${isLatinAd ? '#475569' : Colors.grayMedium};
    flex-shrink: 0;
  }
  & .chatbot-proposal-new-value {
    color: ${accentColor};
    font-weight: 600;
  }
  & .chatbot-proposal-actions {
    display: flex;
    gap: 8px;
    padding: 10px 14px;
    border-top: 1px solid rgba(255, 255, 255, 0.07);
  }
  & .chatbot-proposal-btn-confirm.ant-btn {
    flex: 1;
    background: ${isLatinAd ? 'linear-gradient(to left, #218ae7, #29509f)' : Colors.primary};
    border-color: ${accentColor};
    color: ${Colors.white};
    border-radius: 8px;
    font-size: 13px;
  }
  & .chatbot-proposal-btn-confirm.ant-btn:disabled {
    background: ${isLatinAd ? 'rgba(96, 165, 250, 0.2)' : `${Colors.primary}33`};
    border-color: ${isLatinAd ? 'rgba(96, 165, 250, 0.2)' : `${Colors.primary}33`};
    color: ${isLatinAd ? '#475569' : Colors.grayMedium};
  }
  & .chatbot-proposal-btn-reject.ant-btn {
    flex: 1;
    background: rgba(239, 68, 68, 0.12);
    border-color: rgba(239, 68, 68, 0.35);
    color: ${isLatinAd ? '#f87171' : Colors.red};
    border-radius: 8px;
    font-size: 13px;
  }
  & .chatbot-proposal-btn-reject.ant-btn:hover {
    background: rgba(239, 68, 68, 0.25);
    border-color: rgba(239, 68, 68, 0.6);
  }

  & .chatbot-input-row {
    position: relative;
    z-index: 1;
    display: flex;
    gap: 8px;
    padding: 12px 20px 18px 20px;
    align-items: center;
  }
  & .chatbot-input.ant-input {
    background: ${isLatinAd ? 'rgba(15, 23, 42, 0.6)' : Colors.snow};
    border: 1px solid ${isLatinAd ? 'rgba(96, 165, 250, 0.35)' : `${Colors.primaryAccent}59`};
    border-radius: 12px;
    color: ${isLatinAd ? '#fff' : Colors.coal};
  }
  & .chatbot-input.ant-input::placeholder {
    color: ${isLatinAd ? '#64748b' : Colors.grayMedium};
  }
  & .chatbot-send-button.ant-btn {
    border-radius: 12px;
    background: ${isLatinAd ? 'linear-gradient(to left, #218ae7, #29509f)' : Colors.primary};
    border-color: ${accentColor};
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${Colors.white};
  }
  & .chatbot-send-button.ant-btn[disabled],
  & .chatbot-send-button.ant-btn[disabled]:hover,
  & .chatbot-send-button.ant-btn[disabled]:focus,
  & .chatbot-send-button.ant-btn[disabled]:active {
    background: transparent;
    border-color: ${isLatinAd ? 'rgba(96, 165, 250, 0.15)' : `${Colors.primaryAccent}26`};
    color: ${isLatinAd ? '#475569' : Colors.grayMedium};
    opacity: 0.6;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

export default MapAIChatbotHolder;
